import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, CreditCard, MapPin, RefreshCw,
  Users, Plus, BarChart3, TrendingUp, Printer, DownloadCloud
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import HeroSalesCard from './HeroSalesCard';
import KpiCard from './KpiCard';
import SalesTrendChart from './SalesTrendChart';
import TopCustomersChart from './TopCustomersChart';
import AlertsPanel from './AlertsPanel';
import { api } from '@/api/apiClient';
import { Visit } from '@/api/localStore';
import { prepareOffline } from '@/lib/prepareOffline';
import { cur } from '@/lib/currency';

const CACHE_KEY = 'rep_dashboard_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 min

export default function RepDashboard() {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const { user } = useAuth();
  const { odooSession } = useOdooAuth();

  const loadData = useCallback(async (force = false) => {
    // Try cache first
    if (!force) {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { ts, payload } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          setData(payload.data);
          setLastSync(new Date(ts));
          setLoading(false);
          return;
        }
      }
    }

    setLoading(true);
    setError(null);
    try {
      const [dashRes, visitsData] = await Promise.all([
        api.get('/sales/dashboard/rep'),
        Visit.filter({}, '-visit_date', 5),
      ]);
      if (dashRes?.success) {
        const d = dashRes.data;
        setData(d);
        const now = Date.now();
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now, payload: { data: d } }));
        setLastSync(new Date(now));
      } else {
        setError(dashRes?.error || 'فشل التحميل');
      }
      setVisits(visitsData || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 60s
    const interval = setInterval(() => loadData(true), 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const navigate = useNavigate();
  const fmt = (v) => (v || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtMoney = (v) => `${fmt(v)} ${cur()}`;

  const quickActions = [
    { label: 'العملاء', icon: Users, path: '/customers', color: 'bg-blue-500' },
    { label: 'طلب جديد', icon: ShoppingCart, path: '/orders/new', color: 'bg-emerald-500' },
    { label: 'تحصيل جديد', icon: CreditCard, path: '/collections/new', color: 'bg-amber-500' },
    { label: 'تسجيل زيارة', icon: MapPin, path: '/visits/new', color: 'bg-purple-500' },
    { label: 'طباعة طلبات اليوم', icon: Printer, path: '/orders/print/today', color: 'bg-sky-600' },
  ];

  const today = new Date().toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const name = odooSession?.name || user?.full_name || 'مندوب';

  const handlePrepareOffline = async () => {
    if (!navigator.onLine) { toast.error('لا يوجد اتصال بالإنترنت'); return; }
    setPreparing(true);
    const t = toast.loading('جاري تحضير البيانات للعمل بدون إنترنت...');
    try {
      const { ok } = await prepareOffline();
      toast.dismiss(t);
      if (ok) toast.success('تم تحضير بياناتك للعمل بدون إنترنت ✓');
      else toast.warning('تم التحضير جزئياً — تأكد من الاتصال وأعد المحاولة');
    } catch {
      toast.dismiss(t);
      toast.error('فشل التحضير — تحقق من الإنترنت');
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-black text-foreground">
            مرحباً، {name.split(' ')[0]} 👋
          </h1>
          <p className="text-muted-foreground text-xs mt-0.5">{today}</p>
          {lastSync && (
            <p className="text-muted-foreground text-xs mt-0.5">
              آخر تحديث: {lastSync.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadData(true)}
            disabled={loading}
            className="p-2 rounded-xl border border-border bg-card hover:bg-muted transition-colors"
          >
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center gap-2">
          <span>⚠️ {error}</span>
          <button onClick={() => loadData(true)} className="mr-auto text-red-600 underline text-xs">إعادة المحاولة</button>
        </div>
      )}

      {/* Loading skeletons */}
      {loading && !data && (
        <div className="space-y-3">
          <div className="h-32 bg-muted rounded-2xl animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />)}
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Hero card */}
          <HeroSalesCard
            todaySales={data.today_sales}
            ordersCount={data.today_orders_count}
            trendPct={data.sales_trend_pct}
          />

          {/* Alerts */}
          <AlertsPanel
            overdueCount={data.overdue_invoices_count}
            pendingOrders={data.pending_orders}
          />

          {/* KPI Grid */}
          <div className="grid grid-cols-2 gap-3">
            <KpiCard
              title="التحصيلات"
              value={fmtMoney(data.today_collections)}
              subtitle={`${data.today_collections_count} دفعة`}
              trendPct={data.collections_trend_pct}
              icon={CreditCard}
              iconColor="text-secondary"
              iconBg="bg-secondary/10"
              onClick={() => navigate('/collections')}
            />
            <KpiCard
              title="الطلبات"
              value={`${data.today_orders_count}`}
              subtitle={`متوسط ${fmtMoney(data.aov)}`}
              icon={ShoppingCart}
              iconColor="text-primary"
              iconBg="bg-primary/10"
              onClick={() => navigate('/orders')}
            />
            <KpiCard
              title="الزيارات"
              value={`${visits.length}`}
              subtitle={`مسجلة محلياً`}
              icon={MapPin}
              iconColor="text-purple-600"
              iconBg="bg-purple-100"
              onClick={() => navigate('/visits')}
            />
            <KpiCard
              title="الطلبات المعلقة"
              value={`${data.pending_orders}`}
              subtitle="بانتظار التأكيد"
              icon={BarChart3}
              iconColor="text-amber-600"
              iconBg="bg-amber-100"
              onClick={() => navigate('/orders')}
            />
          </div>

          {/* Charts */}
          <SalesTrendChart data={data.trend_30_days} />
          <TopCustomersChart data={data.top_customers} />

          {/* Top Products */}
          {data.top_products?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <h3 className="font-bold text-foreground mb-3 text-sm">أكثر المنتجات مبيعاً — هذا الشهر</h3>
              <div className="space-y-2">
                {data.top_products.slice(0, 6).map((p, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-muted-foreground w-5 text-left">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                      <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                        <div
                          className="bg-primary h-1.5 rounded-full"
                          style={{ width: `${Math.min(100, (p.revenue / (data.top_products[0]?.revenue || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-left shrink-0">
                      <p className="text-xs font-bold text-foreground">{(p.revenue || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
                      <p className="text-xs text-muted-foreground">{(p.qty || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} وحدة</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Visits */}
          {visits.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-foreground text-sm">آخر الزيارات</h3>
                <Link to="/visits" className="text-primary text-xs font-medium">عرض الكل</Link>
              </div>
              <div className="space-y-2">
                {visits.map(v => (
                  <div key={v.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                    <div className="w-7 h-7 bg-purple-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{v.customer_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(v.visit_date).toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      v.outcome === 'order' ? 'bg-blue-50 text-blue-600' :
                      v.outcome === 'collection' ? 'bg-emerald-50 text-emerald-600' :
                      v.outcome === 'info_only' ? 'bg-amber-50 text-amber-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {v.outcome === 'order' ? 'طلب' : v.outcome === 'collection' ? 'تحصيل' : v.outcome === 'info_only' ? 'متابعة' : 'بدون نتيجة'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-bold text-foreground mb-3">إجراءات سريعة</h2>
        <div className="grid grid-cols-4 gap-2">
          {quickActions.map(({ label, icon: Icon, path, color }) => (
            <Link key={path} to={path}>
              <motion.div
                whileTap={{ scale: 0.95 }}
                className="bg-card border border-border rounded-2xl p-3 flex flex-col items-center gap-2 hover:shadow-md transition-all"
              >
                <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-xs font-semibold text-foreground text-center leading-tight">{label}</span>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}